import { IndexTable, Thumbnail, Badge, Icon, Banner, Card, BlockStack, InlineStack, Text, Box, useBreakpoints, IndexFilters, TextField, Divider, Pagination } from '@shopify/polaris';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { DI, DIProps } from '../../../../../Core';
import useContextLayer from '../../Context/helper';
import { excludeKeysFromValidation, initialVariantStates, resourceName, resourceNameVariantsGrid } from './variantsgridhelper';
import { InfoMinor, SearchMinor } from '@shopify/polaris-icons';
import { defaultImage } from '../../utils/utils';
import useContainerState from '../../Context/ContainerState';
import { getAmazonStatus } from '../../../Settings/Template/Components/FilterProducts/FilterProductsHelper';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '../../../../Panel/useDebounce';
import { getCurrency } from '../BusinessPrice/BusinessPriceN';

interface Props extends DIProps {
    errorExists: boolean;
}

function VariantsGrid(props: Props): ReactElement {

    const { t } = useTranslation();
    let pageCount = 10;
    const [activePage, setActivePage] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [gridLoader, setGridLoader] = useState<boolean>(true);
    const { state } = useContextLayer();
    const [componentState, setComponentState] = useState<any>(initialVariantStates)
    const { containerState } = useContainerState();
    const [search, setSearch] = useState<string>('');

    function variantHeadings(): any {
        return [{ title: t('Image'), id: 0 },
        { title: t('Variant Title'), id: 1 },
        { title: t('SKU'), id: 2 },
        { title: t('Status'), id: 3 },
        { title: t('Barcode'), id: 4 },
        { title: t('Price'), id: 5 },
        { title: t('Inventory'), id: 6 }];
    }

    const getVariantProdOpt = () => {
        return [
            { label: t('Select variant'), value: 'select', disabled: true },
            ...(state?.variants ? state?.variants?.map((variant: any) => {
                return {
                    label: variant?.title,
                    value: variant?.source_product_id,
                    img: variant?.main_image
                }
            }) : [])
        ];
    }

    function ValidateAndRenderBarcode({ barcode, source_product_id }: any) {
        let barcode_fail: boolean = false;
        const [barcodeType, setBarcodeType] = useState("")
        const [popoverActive, setPopoverActive] = useState(true);

        if (containerState?.['onSave']) {
            let { message, data } = containerState?.['onSave'];
            if (data) {
                data?.['barcode_fail'].forEach((elem: any) => {
                    if (elem['source_product_id'] === source_product_id && message === 'Some product saved successfully and for some check Barcode Details')
                        barcode_fail = true
                })
            }
        }

        return (
            <BlockStack gap={'050'}>
                <InlineStack gap="100" blockAlign='center'>
                    {barcode_fail && <div><Icon
                        source={InfoMinor}
                        tone="critical" /></div>}
                    <Text as='span'>{barcode && barcode != "" ? barcode : t("N/A")}</Text>
                </InlineStack>
            </BlockStack>
        );
    }

    let payload: any = {
        "container_id": props?.match?.c_id,
        "remove_parent": true,
        "activePage": activePage,
        "count": pageCount
    }
    if (search != "") {
        payload['or_filter'] = {
            "items.variant_title": {
                "3": search
            },
            "items.sku": {
                "3": search
            },
            "items.barcode": {
                "3": search
            }
        }
    }

    const getCustomValue = (value: any) => {
        return <Text as='p'>{`${getCurrency(props)} ${value}`}</Text>;
    }

    const getVariantsRows = () => {
        setGridLoader(true);
        props.di.POST("connector/product/getRefineVariants", payload).then((e: any) => {
            if (e?.success) {
                setGridLoader(false);
                let modifiedVariantsRows: JSX.Element[] = [];
                let myData = e?.data?.rows;
                myData && myData?.length > 0 && myData?.forEach((element: any) => {
                    let { items } = element;
                    let { status, variant_title, sku, main_image, barcode, source_product_id, optional_attribute, variation_theme_fields, isVariant, price, quantity } = items;
                    if (typeof price == "string") price = +price;

                    modifiedVariantsRows.push(
                        <IndexTable.Row id={source_product_id} key={source_product_id} position={source_product_id}>
                            <IndexTable.Cell>
                                <Thumbnail source={main_image ? (main_image !== '' ? main_image : defaultImage) : defaultImage} alt={main_image} />
                            </IndexTable.Cell>
                            <IndexTable.Cell>
                                <div onClick={() => {
                                    props.history(`variant/${source_product_id}`
                                        , {
                                            state: {
                                                options: getVariantProdOpt(),
                                                source_product_id: source_product_id,
                                                errorExists: props.errorExists
                                            }
                                        }
                                    )
                                }}
                                    style={{ cursor: 'pointer', color: '#2463bc', width: '100px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                    <Text as='span'>{variant_title}</Text>
                                </div>
                            </IndexTable.Cell>
                            <IndexTable.Cell>{sku ?? t("N/A")}</IndexTable.Cell>
                            <IndexTable.Cell><Badge tone={getAmazonStatus(status ?? "Not Listed")}>{status && status != "" ? t(status) : t("Not Listed")}</Badge></IndexTable.Cell>
                            <IndexTable.Cell><ValidateAndRenderBarcode barcode={barcode} source_product_id={source_product_id} /></IndexTable.Cell>
                            <IndexTable.Cell>{getCustomValue(Object.keys?.(price)?.length > 0 ? price.value : price) ?? t("N/A")}</IndexTable.Cell>
                            <IndexTable.Cell>{quantity ?? t("N/A")}</IndexTable.Cell>
                        </IndexTable.Row>
                    )
                })
                componentState['rows'] = [...modifiedVariantsRows];
                setComponentState({ ...componentState });
            } else {
                props.error(t(e?.message) ?? t("Some error occured while fetching data"));
            }
        })
    }

    const getVariantsCount = () => {
        props.di.POST("connector/product/getRefineVariantsCount", payload).then((e: any) => {
            if (e?.success) {
                setTotalCount(e?.data?.count)
            }
            else {
                props.error(t(e?.message) ?? t("Some error occured while fetching data"));
            }
        });
    }

    const debouncedValue = useDebounce(search, 500);

    useEffect(() => {
        getVariantsRows();
        getVariantsCount();
    }, [activePage, debouncedValue]);

    return <>
        {
            Object.keys(state).length > 0 && <div id='tour_variants_grid'>
                <></>
                <Card roundedAbove='sm'>
                    <BlockStack gap="400">
                        <BlockStack gap="100">
                            <InlineStack gap="100">
                                <Text as='h2' fontWeight='semibold'>{t("Variants")}</Text>
                                <Badge>{
                                    containerState?.['initialProductData']?.['variant_attributes'].toString().replace(/,/g, ' , ')
                                }</Badge>
                            </InlineStack>
                            <Text as='p' tone='subdued'>{t("You can check out the complete information of your Amazon listings. Manage and edit the variations of the Amazon Listings here.")}</Text>
                        </BlockStack>

                        <div className='Custom-Polaris-Box'>
                            <Box padding='0' borderRadius='300' borderWidth='025' borderColor='border'>
                                <Box padding={"200"}>
                                    <TextField
                                        prefix={<Icon source={SearchMinor} />}
                                        clearButton
                                        autoComplete='off'
                                        label
                                        labelHidden
                                        placeholder={t("Search with variant's title, sku or barcode")}
                                        value={search}
                                        onChange={(e) => {
                                            e = e.toString().replace(/[!$%^&*()@+|~=`\\#{}\[\]:";'<>?,.\/]/gi, "");
                                            e = e.toString().replace("  ", " ");
                                            e != " " && setSearch(e);
                                        }} onClearButtonClick={() => {
                                            setSearch('');
                                        }} />
                                </Box>
                                <Divider borderColor='border-secondary' />
                                <BlockStack gap={'200'}>
                                    <IndexTable
                                        loading={gridLoader}
                                        key={'IndexTableExample'}
                                        // condensed={useBreakpoints().smDown}
                                        resourceName={resourceNameVariantsGrid}
                                        itemCount={totalCount}
                                        headings={variantHeadings()}
                                        selectable={false}
                                        emptyState={<Banner tone='warning'>{t("No variant(s) found")}</Banner>}
                                    >
                                        {componentState['rows']}
                                    </IndexTable>
                                    <InlineStack align={'center'}>
                                        {!gridLoader && totalCount > 0 ? <Box padding={"200"}>
                                            <Divider />
                                            <Pagination
                                                label={`${activePage}/${Math.ceil(totalCount / pageCount)}`}
                                                hasPrevious={activePage > 1}
                                                onPrevious={() => {
                                                    setActivePage((prev) => prev - 1);
                                                }}
                                                hasNext={activePage < Math.ceil(totalCount / pageCount)}
                                                onNext={() => {
                                                    setActivePage((prev) => prev + 1);
                                                }}
                                            /></Box> : null}
                                    </InlineStack>
                                </BlockStack>
                            </Box>
                        </div>
                    </BlockStack >
                </Card >
                <Divider />
                <Divider />
                {/* <Card>
                    <Card.Section>
                        <Filters
                            queryValue={queryValue}
                            onQueryChange={handleQueryChange}
                            onClearAll={handleClearAll}
                            filters={[]}
                        >
                            <Button>Add filter</Button>
                        </Filters>
                    </Card.Section>
                    <IndexTable
                        resourceName={resourceName}
                        itemCount={variants.length}
                        selectedItemsCount={
                            allResourcesSelected ? 'All' : selectedResources.length
                        }
                        onSelectionChange={handleSelectionChange}
                        headings={[
                            { title: 'Variant Title' },
                            { title: 'SKU' },
                            { title: 'Status' },
                            { title: 'Activity' },
                            { title: 'Barcode' },
                            { title: 'Price' },
                            { title: 'Qty' },
                            { title: '' },
                        ]}
                    >
                        {rows}
                    </IndexTable>
                    <Card.Section>
                        <Pagination
                            hasPrevious
                            onPrevious={() => setActivePage(activePage - 1)}
                            hasNext
                            onNext={() => setActivePage(activePage + 1)}
                        />
                    </Card.Section>
                </Card> */}
            </div >
        }
    </>;
}

export default DI(VariantsGrid);